<template>
  <!-- Loading State -->
  <div v-if="loading" class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
    </div>
  </div>

  <!-- Error State -->
  <PageError 
    v-else-if="error"
    :error="error"
    :errorCode="errorCode" 
  />

  <div class="p-6 dark:text-white" v-else-if="loadshedding">
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBack"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Loadshedding
        </button>
        Loadshedding Details: {{ loadshedding.name }}
        <div class="relative group">
          <button
            type="button"
            class="ml-2 hover:bg-transparent bg-transparent"
            @click="startTutorial"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
          </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </h1>
    </div>
    <div class="overflow-hidden rounded-xl backdrop-blur-md bg-white/80 dark:bg-slate-900/60">
      <table class="w-full text-left">
        <thead class="text-white bg-gradient-to-r from-slate-900/95 to-slate-800/95 dark:from-slate-900/95 dark:to-slate-800/95 backdrop-blur-md border-b border-slate-700/50 dark:border-slate-700/50">
          <tr>
            <th class="py-2 px-4">Field</th>
            <th class="py-2 px-4">Value</th>
          </tr>
        </thead>
        <tbody class="divide-y dark:divide-gray-600/60">
          <tr
            v-for="(field, index) in displayFields"
            :key="field.key"
            :id="'loadshedding-field-' + field.key"
            :class="['border-b dark:border-gray-700', index % 2 === 0 ? 'hover:bg-gray-900/30 transition-colors' : 'hover:bg-gray-900/30 transition-colors']"
          >
            <td class="py-4 px-4">{{ field.label }}</td>
            <td class="py-4 px-4">
              <div class="flex items-center">
                <template v-if="editingField === field.key">
                  <div class="flex items-start gap-2 w-full">
                    <template v-if="field.type === 'select'">
                      <GlassSelect
                        v-model="editValue"
                        :options="field.key === 'site'
                          ? Object.entries(sites).map(([id, name]) => ({ id: Number(id), name }))
                          : tenant.organizations.value"
                        class="flex-grow"
                      />
                    </template>
                    <template v-else>
                      <div class="flex-grow">
                        <input
                          v-model="editValue"
                          @focus="clearFieldError(field.key)"
                          :type="field.type"
                          @keyup.enter="saveEdit(field.key)"
                          :class="[
                            'block w-full px-3 py-2 text-base border focus:outline-none focus:ring-2 sm:text-sm rounded-md',
                            fieldErrors[field.key]
                              ? 'ring-2 ring-red-500 border-red-500 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                          ]"
                        />
                        <FieldError v-if="fieldErrors[field.key]" :message="fieldErrors[field.key]" class="mt-1"/>
                      </div>
                    </template>
                    <div class="flex gap-2 flex-shrink-0">
                      <button
                        type="button"
                        @click.stop="saveEdit(field.key)"
                        @mousedown.prevent
                        class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 px-3 py-2 rounded"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        @click.stop="cancelEdit(field.key)"
                        @mousedown.prevent
                        class="btn bg-gray-500 text-white hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 px-3 py-2 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <span class="flex-grow">
                    {{ formatValue(field.key, loadshedding[field.key]) }}
                  </span>
                  <div class="relative w-1/2">
                    <button
                      v-if="handleNoEdit(field.key)"
                      @click="startEdit(field.key)"
                      class="ml-2 p-1 text-gray-400 hover:text-blue-500 dark:text-blue-500 transition-colors duration-150"
                      @mouseover="showToolTip=true, hoveritem=field.key"
                      @mouseleave="showToolTip=false"
                      >
                      <svg
                        class="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                    <span 
                      v-if="showToolTip && hoveritem === field.key"
                      class="absolute bottom-full whitespace-nowrap mb-2 transform -translate-x-1/2 rounded py-1 px-2 dark:border-gray-700 dark:bg-gray-600 bg-gray-800 text-white text-center"
                      >
                      Change {{field.label}}
                    </span>
                  </div>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  getLoadsheddingById,
  updateLoadshedding,
  getAllLoadsheddings
} from "@/services/loadshedding-service";
import GlassSelect from "@/components/GlassSelect.vue";
import { getSites } from "@/services/siteService";
import { useStore } from "@/store";
import { formatError } from "@/utils/errorMessages";
import PageError from "@/components/PageError.vue";
import FieldError from "@/components/FieldError.vue";
import ExplainConfigButton from "@/components/ExplainConfigButton.vue";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";

export default {
  components: {
    PageError,
    ExplainConfigButton,
    FieldError,
    GlassSelect,
  },
  data() {
    return {
      showToolTip: false,
      loadsheddingDetailSteps: [
        {
          element: '#loadshedding-field-name',
          action: 'display',
          message: 'The name of this loadshedding area.'
        },
        {
          element: '#loadshedding-field-area_id',
          action: 'display',
          message: 'Click the edit icon to update the area ID.'
        },
        {
          element: '#loadshedding-field-org',
          action: 'display',
          message: 'The organisation this loadshedding area belongs to.'
        },
        {
          element: '#loadshedding-field-site',
          action: 'display',
          message: 'Click the edit icon to change the associated site.'
        },
        {
          element: '#loadshedding-field-created_date',
          action: 'display',
          message: 'The date and time this loadshedding area was created.'
        },
      ],
      loadshedding: null,
      sites: {},
      editingField: null,
      editValue: null,
      originalValue: null,
      errorMessage: null,
      noEditFields : ['name', 'org', 'created_date'],
      displayFields: [
        { key: "name", label: "Name", type: "text"},
        { key: "area_id", label: "Area ID", type: "text"},
        { key: "org", label: "Organisation", type: "select"},
        { key: "site", label: "Site", type: "select"},
        { key: "created_date", label: "Created Date"},
      ],
      canChangeLoadshedding: true,
      error: "",
      errorCode: "",
      fieldErrors: {},
    };
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  async mounted() {
    const loadsheddingId = this.$route.params.id;
    try {
      this.checkPermissions();
      const validation = this.tenant.validateSchemaForOperation();
      if (!validation.valid) {
        this.error = "Malformed request. Failed to get loadshedding details!";
        this.errorCode = "422!";
        return;
      } 
      await Promise.all([
        this.fetchLoadshedding(loadsheddingId, validation.schemaName),
        this.fetchSites(validation.schemaName),
        this.tenant.fetchOrganizations(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  },
  watch: {
    user(user){
      if (user) this.checkPermissions()
    }
  },
  computed: {
    user() {
      return useStore.getUser();
    },
  },
  methods: {
    async startTutorial() {
      await this._start(this.loadsheddingDetailSteps);
    },
    async checkPermissions(){
      if (this.user) {
        this.canChangeLoadshedding = await useStore.hasPermission("Can change loadshedding area");
      }
    },
    goBack(){
      this.$router.push({
        name: 'Loadsheddings',
        query: this.tenant.addSchemaParam()
      });
    },
    async fetchLoadshedding(id, schemaName=null) {
      try {
        const loadsheddings = await getAllLoadsheddings(schemaName);
        this.loadshedding = loadsheddings.find(item => item.id == id)
      } catch (error) {
        console.error("Error fetching loadshedding:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    async fetchSites(schemaName=null) {
      try {
        const sites = await getSites(schemaName);
        this.sites = sites.reduce((acc, site) => {
          acc[site.id] = site.name;
          return acc;
        }, {});
      } catch (error) {
        console.error("Error fetching sites:", error);
        throw error;
      }
    },
    formatValue(key, value) {
      if (key === "org") {
        return this.tenant.getOrganizationName(value) || "redacted";
      }
      if (key === "site") {
        return this.sites[value] || "redacted";
      }
      if (key === "created_date") {
        return this.formatDateTime(value);
      }
      return value !== null && value !== undefined ? value : "N/A";
    },
    clearFieldError(fieldName){
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
        delete this.fieldErrors['non_field_errors'];
      }
    },
    startEdit(key) {
      this.editingField = key;
      this.editValue = this.loadshedding[key];
      this.originalValue = this.editValue;
      this.showToolTip = false
    },
    cancelEdit(key) {
      this.clearFieldError(key);
      this.editingField = null;
      this.loadshedding[key] = this.originalValue;
      this.errorMessage = null;
    },
    handleNoEdit(key){
      if (this.canChangeLoadshedding || this.user?.is_staff){
        return !this.noEditFields.includes(key);
    }
    },
    async saveEdit(key) {
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.errorMessage = "Malformed request. Failed to update loadshedding!";
          this.errorCode = "422!";
          return;
        }
        const updatedData = { ...this.loadshedding };
        updatedData[key] = this.editValue;

        // Automatically update the name when area_id or site changes
        if (key === 'area_id' || key === 'site') {
          const selectedSite = this.sites[updatedData.site];
          const selectedOrg = this.tenant.organizations.value[updatedData.org];
          updatedData.name = `${selectedOrg}-${selectedSite}-${updatedData.area_id}`;
        }

        const response = await updateLoadshedding(this.loadshedding.id, updatedData, validation.schemaName);
        this.loadshedding = { ...this.loadshedding, ...response };
        this.editingField = null;
        this.editValue = null;
        this.errorMessage = null;
      } catch (error) {
        console.error("Error updating loadshedding:", error);
        this.fieldErrors = {}; 
        const items = this.displayFields;
        const errData = error?.response?.data;

        items.forEach(element => {
          const fieldError = errData?.[element.key];
          if (fieldError?.[0]) {
            this.fieldErrors[element.key] = fieldError[0]; 
          }
        });
        this.errorMessage = "Error updating data. Please try again.";
      }
    },
    formatDateTime(dateString) {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
  },
};
</script>
